import React, { useRef, useState } from 'react';
import { cn } from '@/utils/cn';

interface UploadProps {
  children?: React.ReactNode;
  beforeUpload?: (file: File) => boolean | Promise<any>;
  onChange?: (info: { file: { status: string; originFileObj: File }; fileList: any[] }) => void;
  customRequest?: (options: { file: File; onSuccess: () => void; onError: () => void }) => void;
  listType?: string;
  fileList?: { url: string; uid: string; name: string }[];
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  /** Legacy prop — ignored (upload list is never shown) */
  showUploadList?: boolean;
}

const Upload: React.FC<UploadProps> = ({
  children,
  beforeUpload,
  onChange,
  customRequest,
  accept,
  multiple,
  disabled,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const processFiles = (files: File[]) => {
    files.forEach((file) => {
      if (beforeUpload) {
        const result = beforeUpload(file);
        if (result === false) return;
      }
      if (customRequest) {
        customRequest({ file, onSuccess: () => {}, onError: () => {} });
      }
      onChange?.({ file: { status: 'done', originFileObj: file }, fileList: [] });
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(Array.from(e.target.files || []));
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    processFiles(Array.from(e.dataTransfer.files || []));
  };

  return (
    <div className={cn(className)}>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={handleChange}
      />
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        data-dragging={isDragging}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setIsDragging(true);
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          if (!disabled) setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragging(false);
        }}
        onDrop={handleDrop}
        className={cn('group outline-none', disabled ? 'opacity-50' : 'cursor-pointer')}
      >
        {children}
      </div>
    </div>
  );
};

export { Upload };
